/**
 * Progressive Bundle Discount System (UI Only)
 *
 * Shows progress toward bundle discounts. Actual discounts are applied
 * automatically by Shopify's automatic discount feature.
 *
 * Tiers (counted on TOTAL QUANTITY of eligible units, not distinct products —
 * 2x the same footrest is 2 units and earns 15%, matching Shopify's evaluator):
 *   - 2 eligible units  = 15% off
 *   - 3 eligible units  = 20% off
 *   - 4+ eligible units = 30% off
 *
 * @see /docs/bundle-discount-architecture.md for full documentation
 */

// ═══════════════════════════════════════════════════════════════════════════
// CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════

const BundleConfig = {
  enabled: true,
  debug: false,  // Set to true in console: BundleConfig.debug = true

  // Which cart lines count toward a tier.
  //
  // This MUST mirror Shopify's server-side entitlement, which is the
  // `promo-eligible` automated collection (rule: TAG NOT_EQUALS "no-promo").
  // When the two disagree the cart promises a discount checkout won't grant.
  //
  // cart.js exposes product_type but NOT tags, so product_type is the proxy.
  // It partitions the live catalogue exactly today: every no-promo product
  // (Almost Perfect, bundles, gift card) carries a type no eligible product
  // uses. Deliberately EXCLUDED, do not re-add:
  //   'Almost Perfect Placemat'    B-grade stock, already marked down
  //   'IKEA Highchair Accessories' bundle listing, already discounted
  //   'Bundle'                     bundle listing (second type, same concept)
  //   'Gift Card'                  discounting stored value prints money
  //   ''                           bundle listing with no type set
  //   'IKEA Highchair Cushion Inflatable Insert'
  //                                support insert; tagged no-promo 2026-08-27.
  //                                It WAS listed here deliberately (a cart
  //                                holding one used to under-count) — that is
  //                                no longer true now the product is excluded
  //                                server-side, and it is the only product of
  //                                its type, so re-adding it would make
  //                                sub-tier carts tease a discount checkout
  //                                will not grant.
  //
  // Truth is still read back from what Shopify actually applied (see
  // _getAppliedTierPercent), so a drift here degrades the nudge rather than
  // the money.
  qualifying: {
    mode: 'product_type',
    types: [
      'IKEA Highchair Placemat',                 // Placemats
      'Highchair Cushion Cover',                 // Cushion covers
      'IKEA Highchair Cushion',                  // Cushions
      'Highchair Leg Wraps',                     // Leg wraps (no IKEA prefix)
      'IKEA Highchair Leg Wraps',                // Leg wraps (with IKEA prefix - legacy)
      'IKEA Highchair Footrest',                 // Footrests
      'Highchair Footrest',                      // Footrests (no IKEA prefix - in case)
    ],
  },

  // BUG-FIX: Removed unused countMode property (always counts total_quantity)

  // Tiers evaluated top-to-bottom, first match wins
  // Must be sorted by minCount DESCENDING
  tiers: [
    { minCount: 4, percent: 30 },
    { minCount: 3, percent: 20 },
    { minCount: 2, percent: 15 },
    { minCount: 1, percent: 0  },
  ],

  // Cart drawer selectors (Little Puku theme specific)
  selectors: {
    // V2.2: Active selectors
    savingsPill: '[data-savings-pill]',
    shippingBadge: '[data-shipping-badge]',
    shippingMessage: '[data-shipping-message]',
    // BUG-FIX: Removed unused V2.1 selectors (cartDrawer, cartFooter, cartTotals, cartItems, shippingProgress)
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// CART OBSERVER
// ═══════════════════════════════════════════════════════════════════════════

const CartObserver = {
  subscribers: [],
  initialized: false,
  _pendingNotify: null,

  subscribe(callback) {
    this.subscribers.push(callback);
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback);
    };
  },

  async getCart() {
    // BUG-FIX: Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10s timeout

    try {
      const response = await fetch('/cart.js', { signal: controller.signal });
      clearTimeout(timeoutId);
      // A throttled (429) or bot-challenged (430) request still resolves. Its
      // body is an error object or an HTML page, and feeding either downstream
      // renders a receipt from garbage. Fail loudly instead — subscribers keep
      // the last good render.
      if (!response.ok) throw new Error('[CartObserver] /cart.js ' + response.status);
      const cart = await response.json();
      if (!cart || !Array.isArray(cart.items)) throw new Error('[CartObserver] /cart.js returned a non-cart payload');
      return cart;
    } catch (error) {
      clearTimeout(timeoutId);
      if (error.name === 'AbortError') {
        console.error('[CartObserver] Cart request timed out');
      }
      throw error;
    }
  },

  init() {
    if (this.initialized) return;
    this.initialized = true;
    this._interceptFetch();
    this._listenForCustomEvents();
  },

  _notify(cart) {
    this.subscribers.forEach(callback => {
      try {
        callback(cart);
      } catch (error) {
        console.error('[CartObserver] Subscriber error:', error);
      }
    });
  },

  _originalFetch: null, // BUG-FIX: Store reference for restore
  _fetchIntercepted: false,

  _interceptFetch() {
    // BUG-FIX: Prevent double-wrapping if already intercepted
    if (this._fetchIntercepted) return;

    this._originalFetch = window.fetch;
    const self = this;

    window.fetch = async function(...args) {
      const response = await self._originalFetch.apply(this, args);
      const url = args[0]?.toString() || '';

      if (self._isCartModifyingRequest(url)) {
        clearTimeout(self._pendingNotify);
        // TIMING: Standard cart animation delay (300ms per cart-system audit)
        self._pendingNotify = setTimeout(async () => {
          try {
            const cart = await self.getCart();
            self._notify(cart);
          } catch (error) {
            console.error('[CartObserver] Failed to fetch cart:', error);
          }
        }, 300);
      }
      return response;
    };

    this._fetchIntercepted = true;
  },

  // BUG-FIX: Restore original fetch when cleaning up
  _restoreFetch() {
    if (this._fetchIntercepted && this._originalFetch) {
      window.fetch = this._originalFetch;
      this._fetchIntercepted = false;
    }
  },

  _listenForCustomEvents() {
    // BUG-FIX: Add error handling for async event listener
    document.addEventListener('cart:updated', async (e) => {
      try {
        const cart = e.detail || await this.getCart();
        this._notify(cart);
        this._reconcileSoon(cart);
      } catch (error) {
        console.error('[CartObserver] Failed to handle cart:updated event:', error);
      }
    });
  },

  // Re-read /cart.js shortly after a cart:updated and re-render if the discount
  // allocations moved.
  //
  // WHY: the drawer's quantity controls go through jQuery $.ajax('/cart/update.js'),
  // which _interceptFetch() cannot see (it patches window.fetch), so the ONLY
  // signal for that path is cart:updated carrying that same response as e.detail.
  // theme.js's removed BUG-044 block was written for exactly this — "Shopify
  // automatic discounts may take a moment to recalculate after quantity changes" —
  // but it queried an element no template renders, so it returned early every time
  // and reconciled nothing. This is the working version of that intent.
  //
  // Only re-notifies when the allocation signature actually CHANGED, so a settled
  // cart costs one request and no re-render, and there is no feedback loop: the
  // refetch dispatches no event.
  _reconcileSoon(cart) {
    clearTimeout(this._reconcileTimer);
    const before = this._allocationSignature(cart);
    this._reconcileTimer = setTimeout(async () => {
      try {
        const fresh = await this.getCart();
        if (this._allocationSignature(fresh) !== before) this._notify(fresh);
      } catch (e) { /* offline or blocked — the rendered state stays as-is */ }
    }, 400);
  },

  _allocationSignature(cart) {
    const lines = (cart && cart.items || []).map(i =>
      i.key + ':' + i.quantity + ':' +
      (i.line_level_discount_allocations || [])
        .map(a => (a.amount || 0) + '/' + ((a.discount_application || {}).title || ''))
        .join(',')
    ).join('|');
    // Cart-LEVEL applications count too. The tier discounts are line-level, but a
    // promo code is not — so a code being applied, removed or re-costed leaves
    // every line allocation untouched, and a line-only signature would call that
    // "unchanged", skip the re-render, and leave the receipt showing the previous
    // promo amount.
    const cartLevel = (cart && cart.cart_level_discount_applications || [])
      .map(d => (d.title || '') + '/' + (d.total_allocated_amount || 0))
      .join(',');
    return lines + '#' + cartLevel;
  },

  _isCartModifyingRequest(url) {
    const cartEndpoints = ['/cart/add', '/cart/update', '/cart/change', '/cart/clear'];
    return cartEndpoints.some(endpoint => url.includes(endpoint));
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// DISCOUNT CALCULATOR
// ═══════════════════════════════════════════════════════════════════════════

const DiscountCalculator = {
  // Units Shopify ACTUALLY entitled — the lines it hung a tier allocation on.
  // Exact by construction, so it needs no knowledge of which products qualify.
  // Returns 0 when the tier came through cart-level (Applies-to all products),
  // because that shape carries no per-line allocations.
  _countEntitledUnits(cart, tiers) {
    let n = 0;
    for (const item of (cart.items || [])) {
      const entitled = (item.line_level_discount_allocations || []).some(
        a => a.amount > 0 && this._isTierApplication(a.discount_application, tiers)
      );
      if (entitled) n += (item.quantity || 0);
    }
    return n;
  },

  calculate(cart, config) {
    // product_type estimate. Only an approximation of the `promo-eligible`
    // collection, because cart.js exposes no tags — so it can over- or
    // under-count whenever the allowlist and the no-promo tag drift apart.
    const estimated = this._countQualifying(cart, config);
    const appliedPercent = this._getAppliedTierPercent(cart, config.tiers);

    // Prefer ground truth. When Shopify applied a tier line-by-line it has
    // already told us exactly which units were eligible, so believe that over
    // the estimate, in both directions — the old code only corrected upward
    // (`appliedPercent > percent`) and so never caught an over-count.
    //
    // LIMIT, deliberate: this can only correct a cart that is already ABOVE the
    // first threshold, because below it Shopify has applied nothing and there
    // are no allocations to read. So a cart of 2 units where one was just
    // tagged `no-promo` — while its product_type is still allowlisted — still
    // estimates 2 and teases the 20% rung, when the truth is 0% now and 15%
    // after one more. Closing that needs eligibility rendered from Liquid.
    // Keep BundleConfig.qualifying.types in step with the tag.
    const entitled = this._countEntitledUnits(cart, config.tiers);
    const count = entitled > 0 ? entitled : estimated;

    let effectiveCount = count;
    let tier = this._getTier(count, config.tiers);
    let percent = tier?.percent || 0;

    // A tier that actually fired outranks anything derived from a count.
    if (appliedPercent > 0) {
      percent = appliedPercent;
      const appliedTier = config.tiers.find(t => t.percent === appliedPercent);
      if (appliedTier) {
        tier = appliedTier;
        // Shopify granting this tier proves at least minCount units were
        // entitled, even when we could not see them (a cart-level discount
        // carries no per-line allocations, so `count` may be an under-count
        // from the product_type estimate). Without this floor, nextTier is
        // derived from a count of 0 and the nudge renders "save 0%".
        effectiveCount = Math.max(count, appliedTier.minCount);
      }
    }

    // Rungs the evidence contradicts are not offerable. If the cart already
    // holds enough units for a richer tier and Shopify still granted less,
    // that tier is paused, scheduled, or gone from admin — promising it would
    // be a promise checkout will not honour.
    const offerable = appliedPercent > 0
      ? config.tiers.filter(t => !(effectiveCount >= t.minCount && t.percent > appliedPercent))
      : config.tiers;

    return {
      count: effectiveCount,
      tier: tier,
      percent: percent,
      nextTier: this._getNextTier(effectiveCount, offerable),
    };
  },

  // True if a discount_application is one of our merchant tier discounts.
  //
  // Deliberately NOT keyed on the title. Titles are merchant-editable in
  // admin, and once the tier became a line-level discount it sits in the same
  // array as the Shopify Bundles app's own allocations — which are also
  // automatic and also plausibly titled "…bundle…". Matching on the structural
  // signature (automatic + percentage + a percent on our ladder) keeps the two
  // apart and survives a rename.
  // Verified against a real cart on this store. A line-level allocation's
  // discount_application looks like:
  //
  //   { amount: 419, discount_application: {
  //       type: "",  key: "daa2f95a-…",  title: "Bundle 15% Off",
  //       value: "15.0",  value_type: "percentage",
  //       allocation_method: "each",  target_selection: "entitled",
  //       target_type: "line_item",  total_allocated_amount: 1018 } }
  //
  // Note `type` is an EMPTY STRING here, not "automatic" — that value only
  // appears on cart-level applications. So we cannot require "automatic";
  // we reject the types that are definitely NOT ours instead.
  //
  // Note also total_allocated_amount is 1018 (the whole A$10.18 discount)
  // repeated on every line it touched, while amount (419) is this line's
  // share. Callers must sum `amount`.
  // Matches ONLY the merchant's tier discounts, which are named to this exact
  // pattern in admin: "Bundle 15% Off" / "Bundle 20% Off" / "Bundle 30% Off".
  //
  // Structural checks alone are not enough. This store has repeatedly run
  // sitewide promotions at 20% ("EOFY SALE 20% OFF", "TWENTYOFF", "FLASHSALE"),
  // which are automatic percentage discounts sitting exactly on the ladder — so
  // percent-matching alone would promote them to "Multi-item bonus", advance the
  // tier nudge, and steal them from the Promo bucket.
  //
  // Anchoring on "Bundle <n>% Off" rather than a loose "bundle" substring also
  // keeps the Shopify Bundles app out: its allocations are titled after the
  // bundle product ("Cushion & Leg Wrap Bundle (SAVE 15%)"), which contains the
  // word but does not match this shape.
  //
  // Trade-off, deliberate: renaming a tier discount in admin stops it being
  // recognised, and the UI falls back to its count-based estimate. That is a
  // graceful degradation (the pre-existing behaviour) rather than the
  // mislabelling a looser test would produce. Keep the admin titles in sync.
  _TIER_TITLE_RE: /^\s*bundle\s+(\d+)%\s*off\s*$/i,

  _isTierApplication(app, tiers) {
    if (!app) return false;
    // Customer-entered codes and hand-applied discounts are never our ladder.
    // `type` is an empty string on line-level allocations, so the type test
    // alone cannot see a collection-scoped code: one titled "Bundle 15% Off" at
    // 15% would slip through and be read as the tier. Presence of a code is
    // decisive, whatever the title claims.
    if (app.code) return false;
    if (app.type === 'discount_code' || app.type === 'manual' || app.type === 'script') return false;
    if (app.value_type !== 'percentage') return false;
    const raw = parseFloat(app.value);
    const p = Math.round(raw);
    if (isNaN(p) || p <= 0) return false;
    // The value must BE the rung exactly. The rungs are whole percentages and
    // the live discounts carry exactly 15.0 / 20.0 / 30.0, so any tolerance at
    // all only widens the door — at 0.01 a 14.99% discount still passed as 15%.
    if (raw !== p) return false;
    if (!tiers.some(t => t.percent === p)) return false;
    // The title's own percent must agree with the discount's value. A discount
    // titled "Bundle 20% Off" carrying value 15.0 is misconfigured, and
    // accepting it would render that title beside a 15% amount. Requiring
    // agreement also stops a near-miss value (19.6 rounding to 20) from
    // impersonating a rung.
    const m = this._TIER_TITLE_RE.exec(app.title || '');
    if (!m) return false;
    return parseInt(m[1], 10) === p;
  },

  // Highest tier percent Shopify ACTUALLY applied to this cart.
  //
  // Reads both shapes, because the same ladder renders differently depending
  // on how the discount is scoped in admin:
  //   Applies-to All products     -> cart_level_discount_applications (across)
  //   Applies-to a collection     -> line_level_discount_allocations  (each)
  // Reading only the first is what silently blinded this reconciliation when
  // the tiers were scoped to `promo-eligible`. Handling both means flipping
  // that setting can never break the UI again.
  _getAppliedTierPercent(cart, tiers) {
    let maxPct = 0;
    const consider = (app) => {
      if (!this._isTierApplication(app, tiers)) return;
      const p = Math.round(parseFloat(app.value));
      if (p > maxPct) maxPct = p;
    };

    for (const app of (cart.cart_level_discount_applications || [])) {
      if (!(app.total_allocated_amount > 0)) continue;
      consider(app);
    }

    for (const item of (cart.items || [])) {
      for (const alloc of (item.line_level_discount_allocations || [])) {
        // alloc.amount is this line's share. Never read the nested
        // total_allocated_amount here — it is the whole discount's total,
        // repeated identically on every line it touched.
        if (!(alloc.amount > 0)) continue;
        consider(alloc.discount_application);
      }
    }
    return maxPct;
  },

  _countQualifying(cart, config) {
    // Guarded like _countEntitledUnits: a cart object can reach here from a
    // `cart:updated` event whose detail is any payload the dispatcher chose.
    const qualifyingItems = (cart.items || []).filter(item => this._isQualifying(item, config));
    // BUG-FIX: Removed 'unique_products' branch - countMode is always 'total_quantity'
    return qualifyingItems.reduce((sum, item) => sum + item.quantity, 0);
  },

  _isQualifying(item, config) {
    // BUG-FIX: Removed unused switch cases - qualifying.mode is always 'product_type'
    return config.qualifying.types.includes(item.product_type);
  },

  _getTier(count, tiers) {
    return tiers.find(tier => count >= tier.minCount);
  },

  // The next rung worth teasing. Skips the {minCount:1, percent:0} sentinel:
  // it is a legitimate ladder entry (it makes _getTier return "no discount"
  // for a 1-item cart) but it is not an offer, and returning it renders
  // "Add 1 more item to save 0%" or an unsatisfiable nudge on a cart made
  // entirely of excluded products.
  _getNextTier(count, tiers) {
    const sorted = [...tiers].sort((a, b) => a.minCount - b.minCount);
    return sorted.find(tier => tier.minCount > count && tier.percent > 0) || null;
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// DISCOUNT AGGREGATOR — bins cart allocations into 4 customer-facing buckets
//
// Sources of savings on a Little Puku cart:
//   1. Sale     — variant compare_at_price > price (item-level)
//   2. Bundle   — line_level_discount_allocations from Shopify Bundles app
//   3. Tier     — our multi-item ladder. Emitted CART-level when scoped to all
//                 products, LINE-level when scoped to a collection; both handled.
//   4. Code     — customer-entered cart-level discount (any other promo)
// ═══════════════════════════════════════════════════════════════════════════

// Product types that represent a bundle LISTING (not a component). Used only to
// decide whether a non-tier line allocation could plausibly be the Shopify
// Bundles app's, versus a promo. Deliberately narrow: mislabelling a promo as a
// bundle is worse than the reverse, so anything not on this list is a promo.
//
// The empty type is NOT listed. Including it meant any product with no
// product_type set — including a future one that is not a bundle at all —
// claimed a sitewide campaign's allocation as "Bundle savings", which is the
// direction this list exists to avoid. The one live untyped product is a bundle
// but carries `no-promo`, so it can never receive an allocation anyway.
const BUNDLE_PRODUCT_TYPES = ['IKEA Highchair Accessories', 'Bundle'];

const DiscountAggregator = {
  /**
   * @param {Object} cart - Shopify /cart.js response
   * @param {Array<{minCount:number, percent:number}>} tiers - BundleConfig.tiers
   * @returns {{
   *   sale:   {amount:number, percent:?number, count:number},
   *   bundle: {amount:number, percent:?number, count:number},
   *   tier:   {amount:number, percent:?number, count:number, title:?string},
   *   code:   {amount:number, percent:?number, count:number, code:?string},
   *   total:  number,
   *   subtotal: number
   * }}
   */
  aggregate(cart, tiers) {
    const result = {
      sale:   { amount: 0, percent: null, count: 0 },
      bundle: { amount: 0, percent: null, count: 0 },
      tier:   { amount: 0, percent: null, count: 0, title: null },
      code:   { amount: 0, percent: null, count: 0, code: null },
      total: 0,
      // True MSRP — sum of compare_at_price × qty when set, else original_line_price.
      // cart.original_total_price does NOT include compare_at savings; it only
      // reflects line-level discount allocations. Verified against Shopify Admin
      // API: bundle variants here have compare_at $52.90 vs price $44.96, but
      // cart.original_total_price = sum of price × qty = $89.92, not $105.80.
      msrp: 0,
      subtotal: cart.original_total_price || cart.total_price || 0,
    };

    // One discount can allocate across many lines; count each discount once.
    const seenLineKeys = new Set();

    // ── Per-line bucket assignment by MECHANISM (not by product type):
    //      compare_at_price > price        → Sale         (variant-level "was/now")
    //      line_level_discount_allocations → Bundle app, OR our Tier when the
    //                                        ladder is scoped to a collection
    //    A bundle listing on sale is still a Sale: the compare_at delta is the
    //    merchant's standard sale-pricing mechanism, regardless of whether the
    //    listing happens to represent a multi-component bundle. Don't reclassify
    //    by product_type — that's marketing-vocabulary leakage into the data path.
    (cart.items || []).forEach((item) => {
      const qty = item.quantity || 1;
      const unitPrice = item.price || 0;
      // compare_at_price is NOT in the /cart.js payload — _enrichCart() grafts it
      // on from /products/{handle}.js before this runs (see CART ENRICHMENT
      // below). Reading this as "cart.js provides it", which the previous comment
      // claimed, is wrong and wasted an investigation: the field really is absent
      // from /cart.js in every market, and the code only works because of the
      // enrichment step. item.original_price is the pre-line-discount price, not
      // compare_at.
      const compareAt = item.compare_at_price || 0;

      // True MSRP per line: compare_at × qty when set, else fall back to
      // original_line_price (which already factors variant pricing + qty).
      if (compareAt > unitPrice) {
        result.msrp += compareAt * qty;
        const lineDiff = (compareAt - unitPrice) * qty;
        result.sale.amount += lineDiff;
        result.sale.count += 1;
      } else {
        result.msrp += (item.original_line_price || unitPrice * qty);
      }

      // Line-level allocations. Two different things land here and they must
      // NOT be merged: the Shopify Bundles app's own allocations, and our
      // multi-item tier once it is scoped to a collection (Applies-to a
      // collection makes Shopify allocate per line rather than across the
      // cart). Split them on the structural signature — see
      // _isTierApplication — so the receipt labels the tier as "Multi-item"
      // with its percent instead of burying it under "Bundle savings".
      //
      // Counts are per DISCOUNT, not per line: one 30% tier touching five
      // lines is one row on the receipt, so dedupe on discount_application.key.
      (item.line_level_discount_allocations || []).forEach((alloc) => {
        if (!(alloc.amount > 0)) return;
        const app = alloc.discount_application || {};
        const key = app.key || app.title || 'unkeyed';
        const isTier = DiscountCalculator._isTierApplication(app, tiers);
        // Three-way, not two. Defaulting every non-tier line allocation to the
        // Bundle bucket labelled a customer's own promo code "Bundle savings"
        // and never echoed the code back — 16 of this store's 17 codes are
        // collection-scoped, and Shopify emits those line-level, so the Promo
        // code bucket was unreachable in practice.
        //
        // Promo is the DEFAULT for anything that is not our tier, and Bundle
        // must be positively identified. A collection-scoped automatic campaign
        // (a BFCM sitewide sale skipping clearance, say) arrives line-level with
        // an empty type and no code — indistinguishable from a code by metadata
        // alone — and the doc already assigns "unrelated automatic campaigns" to
        // Promo. Defaulting the other way labelled such a campaign "Bundle
        // savings", which is the actively misleading direction.
        //
        // Bundle is claimed only when the line is itself a bundle listing, which
        // is the sole thing the Shopify Bundles app allocates against. (It emits
        // no allocations at all on this store today — its bundles carry their
        // discount in the variant price — so this branch is defensive.)
        const isBundleLine = BUNDLE_PRODUCT_TYPES.indexOf(item.product_type || '') !== -1;
        let bucket;
        if (isTier) {
          bucket = result.tier;
        } else if (!isBundleLine || app.type === 'discount_code' || alloc.discount_code || app.code) {
          bucket = result.code;
        } else {
          bucket = result.bundle;
        }

        bucket.amount += alloc.amount;
        if (!seenLineKeys.has(key)) {
          seenLineKeys.add(key);
          bucket.count += 1;
          const pct = app.value_type === 'percentage' ? Math.round(parseFloat(app.value)) : null;
          if (isTier) {
            bucket.percent = pct || bucket.percent;
            bucket.title = bucket.title || app.title;
          } else if (bucket === result.code) {
            bucket.percent = pct || bucket.percent;
            bucket.code = bucket.code || app.code || app.title;
          } else {
            // Name the Bundle row too, rather than an unlabelled percentage.
            bucket.percent = pct || bucket.percent;
          }
        }
      });
    });

    // ── Cart-level: tier (our automatic) vs. promo code (customer-entered)
    (cart.cart_level_discount_applications || []).forEach((app) => {
      if (!(app.total_allocated_amount > 0)) return;

      const pctVal = app.value_type === 'percentage'
        ? Math.round(parseFloat(app.value))
        : null;

      // Same structural test as the line-level branch above, so a tier is
      // classified identically no matter which shape Shopify emitted it in.
      // Anything else (non-automatic, or off-ladder) falls through to Promo
      // code so unrelated automatic campaigns don't contaminate the tier UI.
      if (DiscountCalculator._isTierApplication(app, tiers)) {
        result.tier.amount += app.total_allocated_amount;
        result.tier.percent = pctVal || result.tier.percent;
        result.tier.count += 1;
        result.tier.title = result.tier.title || app.title;
      } else {
        result.code.amount += app.total_allocated_amount;
        result.code.percent = pctVal || result.code.percent;
        result.code.count += 1;
        result.code.code = result.code.code || app.title;
      }
    });

    result.total =
      result.sale.amount +
      result.bundle.amount +
      result.tier.amount +
      result.code.amount;

    return result;
  },
};

// ═══════════════════════════════════════════════════════════════════════════
// UI — V3 receipt rendering
//
// Replaces the V2.3 collection (savings pill, tease banner, progress bar,
// celebration particles) with a single receipt-style breakdown in the cart
// drawer footer. The checkout button glow defined in cart-receipt.css is the
// celebration; no JS particles needed.
//
// Render targets (data-cr-* attributes added in snippets/cart-drawer-v2.liquid):
//   [data-cr-subtotal-row]    — Subtotal (MSRP) row, hidden when no savings
//   [data-cr-breakdown]       — Container for 1-2 expanded breakdown rows
//   [data-cr-summary]         — Collapsed summary button (DEFAULT view; click to expand into [data-cr-breakdown])
//   [data-cr-glow-stack]      —   inner stack of dots inside summary
//   [data-cr-summary-text]    —   "N discounts applied" label
//   [data-cr-summary-amount]  —   total savings amount on summary
//   [data-cr-divider]         — Hairline before Total, hidden when no savings
//   [data-cr-saved]           — "You're saving $X" stamp
//   [data-cr-saved-amount]    —   amount span inside saved stamp
//   [data-cr-nudge]           — Tier nudge ("Add 1 more for 20%")
//   [data-cr-nudge-text]      —   inner text node of the nudge
// ═══════════════════════════════════════════════════════════════════════════

const DiscountUI = {
  // Per-product cache of /products/{handle}.js responses, keyed by handle.
  // Populated lazily on first cart render; subsequent renders use the cache.
  // Why: /cart.js line items DO NOT expose variant.compare_at_price (verified
  // by inspecting an actual cart.js response — the field is absent from line
  // items even when set on the variant). To detect Sale savings (compare_at >
  // price), we have to fetch the product separately and merge variant data in.
  _productCache: new Map(),
  _productInflight: new Map(),

  // Monotonic counter that tags each in-flight render. Because update() awaits
  // /products/{handle}.js fetches, two cart changes fired close together can
  // race: an older update could finish enrichment last and repaint receipt
  // totals/nudges/strikethroughs with stale data. Each call captures its own
  // generation, then bails post-await if a newer call has started.
  _renderGen: 0,

  // tierUnlocked param kept for orchestrator backwards-compat (no-op in V3)
  async update(discountState, cart, _tierUnlocked = false) {
    const myGen = ++this._renderGen;
    await this._enrichCart(cart);
    if (myGen !== this._renderGen) return; // a newer update started; abandon this render
    const buckets = DiscountAggregator.aggregate(cart, BundleConfig.tiers);
    this._lastCart = cart;
    this._bindSummaryToggle();
    this._renderReceipt(buckets);
    this._renderItemStrikethroughs(cart);
    this._renderNudge(discountState, cart);
    this._updateShippingBadge(cart);
  },

  // No-op kept for orchestrator's MutationObserver compatibility (drawer-close hook)
  _cleanupCelebration() {},

  // ─────────────────────────────────────────────────────────────────────────
  // ITEM-LINE STRIKETHROUGH
  //   theme.js renders cart drawer items immediately on /cart/update.js
  //   response, before our /products/{handle}.js enrichment lands. So even
  //   though theme.js:24855 was extended to use compare_at × qty, that
  //   value is undefined at first render. Re-apply here AFTER enrichment.
  // ─────────────────────────────────────────────────────────────────────────

  _renderItemStrikethroughs(cart) {
    if (!cart || !cart.items) return;
    const itemsList = document.querySelector('.cart-drawer-v2__items');
    if (!itemsList) return;

    // One formatter for every amount this module writes. Preferring
    // theme.formatMoney here re-introduced the Australia-centric format for
    // non-AU carts; _money() already falls back to it when no presentment
    // currency is available.
    const fmt = (cents) => this._money(cents);

    // ── Row lookup ─────────────────────────────────────────────────────────
    // A line key is "<variant_id>:<hash>" and the hash is NOT persistent —
    // Shopify's Ajax Cart API says it changes as line characteristics change,
    // "including, but not limited to" properties and discount applications.
    // Measured on this store: bumping quantity so an automatic discount applied
    // changed 42706866372758:c93c7c1b… to 42706866372758:01a34c03…
    //
    // This module runs AFTER an async /products/{handle}.js fetch, so its cart
    // payload can be one revision ahead of the DOM theme.js rendered. Matching
    // on the full key alone then missed and silently skipped the line, leaving a
    // stale or absent "was" price. theme.js hit this first — see its BUG-049
    // comment — and answered it by matching on the variant id.
    //
    // Falling back to variant id UNCONDITIONALLY would trade a silent miss for a
    // silent MISAPPLY: Shopify permits several cart lines with the same variant
    // (different line item properties), and the wrong row would get the wrong
    // strikethrough. So the fallback is only taken when it is unambiguous —
    // exactly one row and exactly one cart line for that variant. Otherwise the
    // line is skipped, which is the same behaviour as before and visibly
    // harmless, rather than wrong.
    const rows = Array.from(itemsList.querySelectorAll('.cart-drawer-v2__item'));
    const rowsByKey = new Map();
    const rowsByVariant = new Map();
    rows.forEach((row) => {
      const k = row.getAttribute('data-line-item-key') || '';
      if (!k) return;
      rowsByKey.set(k, row);
      const vid = k.split(':')[0];
      if (!vid) return;
      if (!rowsByVariant.has(vid)) rowsByVariant.set(vid, []);
      rowsByVariant.get(vid).push(row);
    });
    const itemsPerVariant = new Map();
    cart.items.forEach((i) => {
      const vid = String(i.variant_id);
      itemsPerVariant.set(vid, (itemsPerVariant.get(vid) || 0) + 1);
    });

    cart.items.forEach((item) => {
      const vid = String(item.variant_id);
      let el = rowsByKey.get(item.key || '') || null;
      let viaFallback = false;
      if (!el) {
        const candidates = rowsByVariant.get(vid) || [];
        if (candidates.length === 1 && itemsPerVariant.get(vid) === 1) {
          el = candidates[0];
          viaFallback = true;
        }
      }
      if (!el) return;

      // Taking the fallback means the key differed, i.e. this row was rendered
      // from an OLDER cart revision — so theme.js wrote .cart-item-price from
      // that older payload too. Refreshing only the strikethrough would pair a
      // new "was" with a stale "now". Update both, from the same formatter, so
      // the row is at least internally consistent. On the exact-key path the DOM
      // already agrees with this payload and theme.js owns the current price.
      if (viaFallback) {
        const current = el.querySelector('.cart-item-price');
        if (current) current.innerHTML = fmt(item.final_line_price || 0);
      }

      const original = el.querySelector('.cart-item-price-original');
      if (!original) return;

      const qty = item.quantity || 1;
      const compareAtLine = (item.compare_at_price || 0) * qty;
      const showAmount = Math.max(item.original_line_price || 0, compareAtLine);

      if (showAmount > (item.final_line_price || 0)) {
        original.innerHTML = fmt(showAmount);
        original.style.display = '';
      } else {
        original.style.display = 'none';
      }
    });
  },

  // ─────────────────────────────────────────────────────────────────────────
  // CART ENRICHMENT
  //   /cart.js doesn't expose variant.compare_at_price on line items.
  //   /products/{handle}.js does. Fetch each product once, cache, and merge
  //   variant.compare_at_price onto cart line items so DiscountAggregator
  //   can compute Sale savings without changing its data contract.
  // ─────────────────────────────────────────────────────────────────────────

  async _enrichCart(cart) {
    const items = (cart && cart.items) || [];
    if (!items.length) return;
    const uniqueHandles = [...new Set(items.map(i => i.handle).filter(Boolean))];
    await Promise.all(uniqueHandles.map(h => this._ensureProductCache(h)));
    items.forEach(item => {
      if (item.compare_at_price != null) return; // already enriched
      const product = this._productCache.get(item.handle);
      if (!product || !product.variants) return;
      const variant = product.variants.find(v => v.id === item.variant_id);
      if (variant && variant.compare_at_price != null) {
        item.compare_at_price = variant.compare_at_price;
      }
    });
  },

  async _ensureProductCache(handle) {
    if (this._productCache.has(handle)) return;
    if (this._productInflight.has(handle)) return this._productInflight.get(handle);
    // Use Shopify.routes.root so the fetch hits the same locale/market context
    // as /cart.js (e.g. /en-au/products/...js on a market subpath). Without
    // this prefix, prices can come back in a different presentment currency
    // than the cart and produce wrong compare_at-based savings.
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = root.replace(/\/?$/, '/') + 'products/' + encodeURIComponent(handle) + '.js';
    const promise = (async () => {
      try {
        const r = await fetch(url, { headers: { 'Accept': 'application/json' } });
        if (!r.ok) return;
        const product = await r.json();
        this._productCache.set(handle, product);
      } catch (e) {
        // Network error — leave cache unset; next cart change will retry.
      } finally {
        this._productInflight.delete(handle);
      }
    })();
    this._productInflight.set(handle, promise);
    return promise;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // RECEIPT
  // ─────────────────────────────────────────────────────────────────────────

  _renderReceipt(buckets) {
    const subtotalRow = document.querySelector('[data-cr-subtotal-row]');
    // [data-cr-subtotal-amount], not .cart-item-original-total-price: that class
    // is written by theme.js and cart-recommendations.js with a DIFFERENT value
    // (cart.original_total_price), so sharing it made this row's contents depend
    // on which module rendered last.
    const subtotalAmt = subtotalRow && subtotalRow.querySelector('[data-cr-subtotal-amount]');
    const breakdown   = document.querySelector('[data-cr-breakdown]');
    const summary     = document.querySelector('[data-cr-summary]');
    const summaryText = document.querySelector('[data-cr-summary-text]');
    const summaryAmt  = document.querySelector('[data-cr-summary-amount]');
    const summaryStack = document.querySelector('[data-cr-glow-stack]');
    const divider     = document.querySelector('[data-cr-divider]');
    const saved       = document.querySelector('[data-cr-saved]');
    const savedAmt    = document.querySelector('[data-cr-saved-amount]');

    // Bail out gracefully on pages without the receipt markup
    if (!breakdown || !summary) return;

    const activeBuckets = this._activeBuckets(buckets);
    const totalSavings  = buckets.total;

    // Fail closed. The receipt's whole claim is that
    //   MSRP  −  the savings we itemise  =  what you pay.
    // Everything here is integer cents, so that identity is exact when our
    // bucketing is right. If it ever isn't — a discount landing in two arrays
    // and being counted twice, an enrichment returning another currency, a
    // Shopify payload shape we have not seen — showing a confident breakdown
    // built on it is worse than showing none. Suppress the itemisation and let
    // the Total (which is cart.total_price, straight from Shopify) stand alone.
    const cartTotal = this._lastCart && this._lastCart.total_price;
    if (typeof cartTotal === 'number' && (buckets.msrp - totalSavings) !== cartTotal) {
      console.error('[BundleDiscounts] receipt does not reconcile; suppressing breakdown',
        { msrp: buckets.msrp, savings: totalSavings, expected: cartTotal });
      if (subtotalRow) subtotalRow.hidden = true;
      if (divider)     divider.hidden     = true;
      if (saved)       saved.hidden       = true;
      breakdown.hidden = true;
      breakdown.innerHTML = '';
      summary.hidden = true;
      summary.setAttribute('aria-expanded', 'false');
      return;
    }

    const hasSavings    = totalSavings > 0;

    if (subtotalRow) subtotalRow.hidden = !hasSavings;
    if (divider)     divider.hidden     = !hasSavings;
    if (saved)       saved.hidden       = !hasSavings;
    if (savedAmt && hasSavings) savedAmt.innerHTML = this._money(totalSavings);

    // Mobile 2-line receipt: "(N items)" chip inside the Total label.
    // The span ([data-cr-item-count] in cart-drawer-v2.liquid) is display:none
    // on desktop (cart-drawer-v2.css), so this is a no-op visually >640px.
    // this._lastCart is set by update() before _renderReceipt is called.
    const countEl = document.querySelector('[data-cr-item-count]');
    if (countEl) {
      const n = (this._lastCart && this._lastCart.item_count) || 0;
      countEl.textContent = n > 0 ? '(' + n + (n === 1 ? ' item)' : ' items)') : '';
    }

    // Override the subtotal MSRP amount with our computed MSRP (which includes
    // compare_at savings). theme.js writes cart.original_total_price to the
    // same span on cart change; we override AFTER its 300ms refetch via the
    // CartObserver hook, so this value sticks.
    //
    // theme.js (line ~24902) hides .cart-item-original-total-price via .hide()
    // when cart.original_total_price <= cart.total_price. For compare_at-only
    // savings (no line/cart discount allocations) that comparison is true and
    // the span has display:none, even though we DO have savings to surface.
    // Clear the inline display so the override is actually visible.
    if (subtotalAmt && hasSavings && buckets.msrp > 0) {
      subtotalAmt.innerHTML = this._money(buckets.msrp);
      subtotalAmt.style.display = '';
    }

    if (!hasSavings) {
      breakdown.hidden = true;
      breakdown.innerHTML = '';
      summary.hidden = true;
      // Reset to collapsed default for the next savings-bearing render.
      // Otherwise a cart that goes discounted → no-discounts → discounted
      // would skip the collapsed summary because aria-expanded stuck at 'true'.
      summary.setAttribute('aria-expanded', 'false');
      return;
    }

    // Collapsed-with-sequential-glow is the primary UI per design intent
    // (see /tmp/cart-footer-mockups.html). Whenever discounts exist, render
    // "N discount(s) applied" with the dot-pulse wave and let the user click
    // to reveal the breakdown rows. aria-expanded='true' (toggled by the
    // bound summary click handler) sticks the breakdown open.
    const userExpanded = summary.getAttribute('aria-expanded') === 'true';
    this._syncSummaryToggleLabel(summary);

    // Breakdown rows are rendered in BOTH states now (previously only when
    // expanded). Desktop visibility is unchanged — the [hidden] attribute
    // still governs display there — but the mobile 2-line receipt CSS
    // (cart-drawer-v2.css ≤640px) overrides [hidden] on a non-empty
    // [data-cr-breakdown] to surface the per-type savings segments inline.
    breakdown.innerHTML = activeBuckets.map(b => this._breakdownRow(b)).join('');

    // The summary button is now rendered in BOTH states — it stays visible while
    // expanded so it can be clicked to collapse again (hiding it was what made the
    // receipt one-way). That means its contents must be maintained in both states
    // too: while it was hidden-when-expanded, updating it only in the collapsed
    // branch was harmless, but a visible button showing the previous discount
    // count and savings beside a freshly recalculated breakdown is not.
    const n = activeBuckets.length;
    // Localized copy comes from data-str-* attributes (rendered server-side via cart.discounts.*),
    // with %count% substituted client-side. Falls back to English if the attrs are absent.
    const tpl = (n === 1
      ? summaryText.getAttribute('data-str-one') || '%count% discount applied'
      : summaryText.getAttribute('data-str-other') || '%count% discounts applied');
    if (summaryText) summaryText.textContent = tpl.replace('%count%', n);
    if (summaryAmt) summaryAmt.innerHTML = '−' + this._money(totalSavings);
    this._renderGlowStack(summaryStack, activeBuckets);

    summary.hidden = false;
    breakdown.hidden = !userExpanded;
  },

  _activeBuckets(buckets) {
    // shortLabel feeds the mobile 2-line receipt segments ("Sale $13.68 ·
    // Bundle 15% $5.58"); label remains the desktop expanded-breakdown text.
    const out = [];
    if (buckets.sale.amount > 0) {
      out.push({ type: 'sale',   label: 'Sale savings',     shortLabel: 'Sale',       amount: buckets.sale.amount,   percent: null });
    }
    if (buckets.bundle.amount > 0) {
      out.push({ type: 'bundle', label: 'Bundle savings',   shortLabel: 'Bundle',     amount: buckets.bundle.amount, percent: buckets.bundle.percent });
    }
    if (buckets.tier.amount > 0) {
      out.push({ type: 'tier',   label: 'Multi-item bonus', shortLabel: 'Multi-item', amount: buckets.tier.amount,   percent: buckets.tier.percent });
    }
    if (buckets.code.amount > 0) {
      out.push({ type: 'code',   label: 'Promo code',       shortLabel: 'Promo',      amount: buckets.code.amount,   percent: buckets.code.percent, code: buckets.code.code });
    }
    return out;
  },

  _breakdownRow(bucket) {
    const escape = (s) => String(s).replace(/[&<>"']/g, ch => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[ch]);
    let labelInner = escape(bucket.label);
    if (bucket.type === 'code' && bucket.code) {
      labelInner = 'Promo code <span class="cr-code">' + escape(bucket.code) + '</span>';
    }
    // Dual label spans (ADDITIVE — no existing class renamed/removed):
    // .cr-lbl-full is the desktop text (visible by default), .cr-lbl-short is
    // the compact mobile-segment text (display:none by default; the ≤640px
    // block in cart-drawer-v2.css swaps them).
    const label =
      '<span class="cr-lbl-full">' + labelInner + '</span>' +
      '<span class="cr-lbl-short">' + escape(bucket.shortLabel || bucket.label) + '</span>';
    const pct = bucket.percent
      ? '<span class="cr-pct cr-pct--' + bucket.type + '">−' + bucket.percent + '%</span>'
      : '';
    return (
      '<div class="cr-breakdown__row">' +
        '<span class="cr-breakdown__label">' +
          '<span class="cr-dot cr-dot--' + bucket.type + '"></span>' +
          label + (pct ? ' ' + pct : '') +
        '</span>' +
        '<span class="cr-breakdown__amount">−' + this._money(bucket.amount) + '</span>' +
      '</div>'
    );
  },

  _renderGlowStack(stack, activeBuckets) {
    if (!stack) return;
    stack.innerHTML = activeBuckets
      .map(b => '<span class="cr-glow-dot cr-glow-dot--' + b.type + '"></span>')
      .join('');
  },

  // The markup carries data-str-show / data-str-hide on [data-cr-summary-toggle]
  // for exactly this, but nothing read data-str-hide and the handler only ever
  // set aria-expanded='true' — so the receipt expanded on the first click and
  // then could not be closed, while its label went on saying "Show details".
  _syncSummaryToggleLabel(summary) {
    const toggle = summary.querySelector('[data-cr-summary-toggle]');
    if (!toggle) return;
    const expanded = summary.getAttribute('aria-expanded') === 'true';
    const label = expanded
      ? toggle.getAttribute('data-str-hide')
      : toggle.getAttribute('data-str-show');
    if (label) toggle.textContent = label;
  },

  _bindSummaryToggle() {
    if (this._summaryBound) return;
    const summary = document.querySelector('[data-cr-summary]');
    if (!summary) return;
    summary.addEventListener('click', () => {
      const expanded = summary.getAttribute('aria-expanded') === 'true';
      summary.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      this._syncSummaryToggleLabel(summary);
      const lastCart = this._lastCart;
      if (lastCart) {
        const buckets = DiscountAggregator.aggregate(lastCart, BundleConfig.tiers);
        this._renderReceipt(buckets);
      }
    });
    this._summaryBound = true;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // NUDGE — next-tier teaser. Hides at the max tier or empty cart.
  // ─────────────────────────────────────────────────────────────────────────

  _renderNudge(state, cart) {
    const nudge = document.querySelector('[data-cr-nudge]');
    const text  = document.querySelector('[data-cr-nudge-text]');
    if (!nudge || !text) return;

    const itemCount = cart.item_count || 0;
    const next = state.nextTier;
    const currentPct = state.percent || 0;

    if (itemCount === 0 || !next) {
      nudge.hidden = true;
      return;
    }

    const remaining = next.minCount - state.count;
    if (remaining <= 0) {
      nudge.hidden = true;
      return;
    }

    // The sentence says "eligible" because the ladder counts only units in the
    // `promo-eligible` collection: a shopper holding clearance placemats or a
    // bundle can add another one, watch the count not move, and conclude the
    // cart is broken. state.count is already eligible-only, so this makes the
    // words agree with the arithmetic.
    //
    // Templates come from data-str-* on the nudge element rather than being
    // written here, so the six non-English storefronts get their own wording AND
    // their own word order. English is the fallback if an attribute is absent.
    const plural = remaining === 1 ? 'one' : 'other';
    const key = currentPct === 0
      ? 'data-str-unlock-' + plural   // 1-qualifying-item curiosity gap: no % yet
      : 'data-str-save-' + plural;
    // %count% / %percent%, NOT {{ count }}: Shopify's `t` filter treats {{ … }}
    // inside a locale value as its own interpolation variable and consumes it,
    // so the placeholders would never survive to reach this code. The existing
    // cart.discounts.summary_* strings use the %-style for the same reason.
    const fallback = currentPct === 0
      ? 'Add <strong>%count% more eligible item' + (remaining === 1 ? '' : 's') +
        '</strong> to unlock a <strong>surprise discount</strong>'
      : 'Add <strong>%count% more eligible item' + (remaining === 1 ? '' : 's') +
        '</strong> to save %percent%';

    const template = nudge.getAttribute(key) || fallback;
    text.innerHTML = template
      .replace(/%count%/g, String(remaining))
      .replace(/%percent%/g,
               '<span class="cr-nudge__next-pct">' + next.percent + '%</span>');
    nudge.hidden = false;
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SHIPPING BADGE (preserved from V2.2 — geo-personalized free shipping)
  // ─────────────────────────────────────────────────────────────────────────

  // Per-country shipping rule, mirroring snippets/geo-content-renderer.liquid:
  // look up the ISO country, else fall back to the "default" (rest-of-world)
  // entry. Returns null when the metafield is absent, so callers can stay quiet
  // instead of inventing a threshold.
  _shippingRule(country) {
    const rules = window.LP_SHIPPING_RULES;
    if (!rules || typeof rules !== 'object') return null;
    return rules[country] || rules.default || null;
  },

  _getLocationName() {
    const countryNames = {
      AU: 'Australia',
      US: 'the US',
      NZ: 'New Zealand',
      GB: 'the UK',
      CA: 'Canada',
      DE: 'Germany',
      FR: 'France',
      IT: 'Italy',
      NL: 'the Netherlands',
      ES: 'Spain',
      IE: 'Ireland',
      AT: 'Austria',
      BE: 'Belgium',
      DK: 'Denmark',
      FI: 'Finland',
      GR: 'Greece',
      PL: 'Poland',
      PT: 'Portugal',
      SE: 'Sweden',
      IS: 'Iceland',
      GL: 'Greenland',
      HR: 'Croatia',
      JP: 'Japan',
      SG: 'Singapore',
      default: 'you'
    };
    const country = window.MarketContext?.marketCountry ||
                    window.GeoModule?.getCountry?.() ||
                    window.Shopify?.country || 'default';
    return countryNames[country] || countryNames.default;
  },

  _updateShippingBadge(cart) {
    const badge = document.querySelector(BundleConfig.selectors.shippingBadge);
    const messageEl = document.querySelector(BundleConfig.selectors.shippingMessage);
    if (!badge || !messageEl) return;

    const country = window.MarketContext?.marketCountry ||
                    window.GeoModule?.getCountry?.() ||
                    window.Shopify?.country || 'AU';

    const rule = this._shippingRule(country);

    // Say nothing rather than something wrong. The old code fell back to a
    // hardcoded 6500 — the AU threshold — whenever the lookup missed, which it
    // did for the US (absent from the flat map entirely) and for every EU
    // country (the flat map is keyed by market bucket 'EU', not ISO code). US
    // shoppers were told they needed A$65 when their real threshold was a
    // fraction of that. No number is quoted here on purpose: thresholds move
    // (US went $25 -> $35 on 2026-08-21) and a comment cannot track them.
    if (!rule || rule.threshold_cents === null || rule.threshold_cents === undefined) {
      badge.hidden = true;
      return;
    }

    const threshold = rule.threshold_cents;
    const location = this._getLocationName();
    const isRow = !window.LP_SHIPPING_RULES?.[country];
    // Mirror geo-content-renderer's rule EXACTLY: it appends the ISO code for
    // rest-of-world and for '$' markets other than Australia's own (US, NZ, CA).
    // An earlier pass here appended it for rest-of-world only, so the
    // server rendered "Free shipping over €45 EUR" while this badge rendered
    // "€45.00" — a visible disagreement in the same drawer for every non-AU
    // market, which is a third of all orders. AU omits the code because the
    // market-scoped storefront already implies it.
    // Only '$' is ambiguous (AUD vs USD/NZD/CAD): '€45 EUR' read as a doubled
    // currency (register A51), so markets with their own symbol drop the code too.
    const needsCurrencyCode = isRow || (country !== 'AU' && (rule.currency_symbol || '$') === '$');

    // threshold_cents 0 means unconditional free shipping (see
    // geo-content-renderer.liquid). `|| 6500` used to swallow this too.
    if (threshold === 0) {
      messageEl.innerHTML = `Free shipping to <strong>${location}</strong>!`;
      badge.hidden = false;
      badge.classList.add('cart-drawer-v2__shipping-badge--qualified');
      return;
    }

    // A `cart:updated` payload that carries no usable total (some emitters send a
    // partial object) previously produced remaining = NaN, which is neither 0 nor
    // > 0 — so the badge fell through to the qualified branch and told the shopper
    // "Free shipping to <country>!" when they had not reached the threshold.
    // Falsely promising free shipping is the worst failure this badge can have.
    const totalPrice = cart && typeof cart.total_price === 'number' ? cart.total_price : null;
    if (totalPrice === null) {
      badge.hidden = true;
      return;
    }

    const remaining = Math.max(0, threshold - totalPrice);

    // Format from the RULE, mirroring geo-content-renderer, and deliberately NOT
    // through this._money(). Both shop-level formatters are Australia-centric:
    // layout/theme.liquid sets Shopify.money_format from
    // shop.money_with_currency_format, which is the literal string
    // "${{amount}} AUD", and theme.js's formatMoney carries the shop's plain
    // "${{amount}}". Either one renders Germany's EUR 45 threshold as "$45.00
    // AUD" or "$45.00" — the wrong currency outright, not merely a missing code.
    // The shipping rule already carries the correct symbol and ISO code per
    // market, so it is the only trustworthy source here.
    //
    // Cents are shown only when non-zero, matching the server's threshold
    // formatting ("$65", but "$49.95").
    const money = (cents) => {
      const dollars = Math.floor(cents / 100);
      const remainder = cents % 100;
      const amount = remainder === 0
        ? String(dollars)
        : dollars + '.' + String(remainder).padStart(2, '0');
      const symbol = rule.currency_symbol || '$';
      return needsCurrencyCode && rule.currency
        ? symbol + amount + ' ' + rule.currency
        : symbol + amount;
    };

    if (!cart || cart.item_count === 0) {
      messageEl.innerHTML = `Free shipping to <strong>${location}</strong> over ${money(threshold)}`;
      badge.hidden = false;
      badge.classList.remove('cart-drawer-v2__shipping-badge--qualified');
    } else if (remaining > 0) {
      messageEl.innerHTML = `<span class="cart-drawer-v2__shipping-amount">${money(remaining)}</span> away from Free Shipping to <strong>${location}</strong>`;
      badge.hidden = false;
      badge.classList.remove('cart-drawer-v2__shipping-badge--qualified');
    } else {
      messageEl.innerHTML = `Free shipping to <strong>${location}</strong>!`;
      badge.hidden = false;
      badge.classList.add('cart-drawer-v2__shipping-badge--qualified');
    }
  },

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  // Format a CART amount in the cart's own presentment currency.
  //
  // The shop-level formatters cannot do this. layout/theme.liquid sets
  // Shopify.money_format from shop.money_with_currency_format — the literal
  // string "${{amount}} AUD" — and window.wetheme.formatMoney carries the shop's
  // plain "${{amount}}". Both are Australia's. Running a EUR cart through
  // either renders "€45" as "$45.00 AUD" or "$45.00": the wrong currency, on
  // every non-AU market, which is a third of orders.
  //
  // cart.js returns `currency` (the presentment currency), so Intl can format
  // it correctly. currencyDisplay:'narrowSymbol' is what makes the output match
  // what Liquid's `money` filter renders beside it — the default "symbol" form
  // gives "A$65.00" for AUD where the server says "$65.00". narrowSymbol is
  // ES2020 and throws RangeError on older engines, hence the guarded fallback.
  //
  // `symbol` is a caller-supplied fallback (e.g. a shipping rule's own symbol)
  // used only when no presentment currency is known.
  _money(cents, symbol) {
    const currency = this._lastCart && this._lastCart.currency;
    if (currency) {
      // 'en', not the page language — see the note in theme.js formatMoney.
      // de-DE would render EUR as "45,00 €" beside server amounts written
      // "€45.00", putting two money styles in one drawer.
      const locale = 'en';
      try {
        return new Intl.NumberFormat(locale, {
          style: 'currency', currency, currencyDisplay: 'narrowSymbol',
        }).format(cents / 100);
      } catch (e) {
        try {
          return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
        } catch (e2) { /* unknown currency code — fall through */ }
      }
    }
    // No theme-formatter branch here, deliberately. The old one tested
    // window.theme, which this theme never defines (the object is window.wetheme),
    // so it was dead code reading as coverage. The live object is not used either:
    // wetheme.formatMoney returns money HTML with an sr-only "Cart Subtotal" label
    // baked in — right for a cart total, wrong everywhere THIS helper is used,
    // which is savings stamps and per-discount breakdown rows. Callers write
    // innerHTML = '-' + _money(...), so that markup would nest and a screen reader
    // would announce "Promo code SAVE10 Cart Subtotal $5.00".
    // Everything below returns plain text.
    if (window.Shopify?.money_format) {
      const fmt = window.Shopify.money_format;
      const amount = (cents / 100).toFixed(2);
      return fmt
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
    }
    // Last resort. Group thousands so a four-figure amount does not render
    // "$1234.00"; the separator matches the en-AU/en-US convention the rest of
    // the theme uses.
    const whole = Math.floor(Math.abs(cents) / 100);
    const centsPart = String(Math.abs(cents) % 100).padStart(2, '0');
    const grouped = String(whole).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return (cents < 0 ? '-' : '') + (symbol || '$') + grouped + '.' + centsPart;
  },

};


// ═══════════════════════════════════════════════════════════════════════════
// ORCHESTRATOR
// ═══════════════════════════════════════════════════════════════════════════

const BundleDiscountOrchestrator = {
  _unsubscribe: null,
  _lastPercent: 0, // V2.3: Track previous tier for transition detection
  _isInitialLoad: true, // V2.3: Skip celebration on page load

  init(config) {
    if (!config.enabled) {
      if (config.debug) console.log('[Bundle] System disabled via config');
      return;
    }

    this._unsubscribe = CartObserver.subscribe(async (cart) => {
      this._handleCartChange(cart, config);
    });

    CartObserver.init();
    this._processCurrentCart(config);

    // V2.3: Clean up celebration particles when drawer closes.
    // The slideout library adds 'slideout-panel-hidden' class on close.
    // Watch for that class to appear on the cart wrapper.
    const cartWrapper = document.getElementById('cartSlideoutWrapper');
    if (cartWrapper) {
      const observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
          if (m.type === 'attributes' && m.attributeName === 'class' &&
              cartWrapper.classList.contains('slideout-panel-hidden')) {
            DiscountUI._cleanupCelebration();
          }
        }
      });
      observer.observe(cartWrapper, { attributes: true, attributeFilter: ['class'] });
    }

    if (config.debug) console.log('[Bundle] System initialized');
  },

  // BUG-FIX: Add destroy method to prevent memory leaks
  destroy() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  },

  async _handleCartChange(cart, config) {
    const discountState = DiscountCalculator.calculate(cart, config);

    // V2.3: Detect tier transitions
    const previousPercent = this._lastPercent;
    const newPercent = discountState.percent;
    const tierUnlocked = !this._isInitialLoad && newPercent > previousPercent && newPercent > 0;
    const tierDowngraded = newPercent < previousPercent;
    this._lastPercent = newPercent;
    this._isInitialLoad = false;

    if (config.debug) {
      console.log('[Bundle] Cart analyzed:', {
        totalItems: cart.items.length,
        qualifyingProducts: discountState.count,
        currentTier: newPercent + '%',
        previousTier: previousPercent + '%',
        tierUnlocked,
        nextTier: discountState.nextTier?.percent ? discountState.nextTier.percent + '%' : 'max'
      });
    }

    // Update UI — async because we may need to fetch /products/{handle}.js to
    // enrich line items with variant.compare_at_price (cart.js doesn't expose it).
    try {
      await DiscountUI.update(discountState, cart, tierUnlocked);
    } catch (err) {
      console.error('[Bundle] DiscountUI.update failed:', err);
    }

    // Emit enhanced event for analytics (after UI render so listeners see settled state)
    document.dispatchEvent(new CustomEvent('bundle:discount-updated', {
      detail: { discountState, cart, tierUnlocked, tierDowngraded, previousPercent }
    }));
  },

  async _processCurrentCart(config) {
    try {
      const cart = await CartObserver.getCart();
      this._handleCartChange(cart, config);
    } catch (error) {
      console.error('[Bundle] Error processing current cart:', error);
    }
  }
};

// ═══════════════════════════════════════════════════════════════════════════
// INITIALIZATION
// ═══════════════════════════════════════════════════════════════════════════

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    BundleDiscountOrchestrator.init(BundleConfig);
  });
} else {
  BundleDiscountOrchestrator.init(BundleConfig);
}

// Expose for debugging
window.BundleConfig = BundleConfig;
window.BundleDiscountOrchestrator = BundleDiscountOrchestrator;
